import { useState, useEffect, useCallback } from 'react';

interface UseLocalPaginationOptions {
  defaultPageSize?: number;
  defaultSortField?: string;
  defaultSortDirection?: 'asc' | 'desc';
}

const SORT_KEY = 'sortKey';

export const useLocalPagination = ({
  defaultPageSize = 25,
  defaultSortField = 'firstName',
  defaultSortDirection = 'asc',
}: UseLocalPaginationOptions = {}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSortDirection);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);

  // Handle sort changes
  useEffect(() => {
    const currentSortKey = `${sortField}-${sortDirection}`;

    // Get stored sort key
    const storedSortKey = sessionStorage.getItem(SORT_KEY);

    // Only reset if sort actually changed
    if (storedSortKey !== currentSortKey) {
      resetPaginationState();
      sessionStorage.setItem(SORT_KEY, currentSortKey);
    }
  }, [sortField, sortDirection]);

  const resetPaginationState = useCallback(() => {
    setCurrentPage(1);
    setIsLoadingNextPage(false);
  }, []);

  const updatePage = useCallback((page: number) => {
    const validPage = Math.max(1, page);
    setCurrentPage(validPage);
    setIsLoadingNextPage(true);
  }, []);

  const updatePageSize = useCallback(
    (size: number) => {
      setPageSize(size);
      resetPaginationState();
    },
    [resetPaginationState]
  );

  const updateSort = useCallback(
    (field: string, direction: 'asc' | 'desc') => {
      setSortField(field);
      setSortDirection(direction);
      resetPaginationState();
    },
    [resetPaginationState]
  );

  const setLoading = useCallback((loading: boolean) => {
    setIsLoadingNextPage(loading);
  }, []);

  return {
    currentPage,
    pageSize,
    sortField,
    sortDirection,
    isLoadingNextPage,
    updatePage,
    updatePageSize,
    updateSort,
    setLoading,
    resetPagination: resetPaginationState,
  };
};

export default useLocalPagination;
